import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { api, type ApiError, type PreviewLeadConversionResult } from '@whinerp/client';
import { ConfirmDialog, ErrorState, Icon, Num, Panel, Skeleton, useToast } from '@whinerp/ui';
import { formatMoney } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';

/**
 * SC-11 Convert lead.
 *
 * Three records out of one, so the screen shows all three before creating any.
 * The preview is a round trip rather than something assembled here: the server
 * computes it with the same function that performs the conversion, so what is
 * on the cards is what will happen, not a client's guess at it.
 *
 * The confirm button counts what it is about to create - "Create all three",
 * "Create both" - because a button labelled "Convert" tells a person nothing
 * about the size of what they are agreeing to.
 */

type Plan = PreviewLeadConversionResult;

function ConvertLeadScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const params = useParams();
  const leadId = params['id'] ?? '';

  const [createDeal, setCreateDeal] = useState(true);
  const [dealName, setDealName] = useState('');
  const [dealAmount, setDealAmount] = useState('');

  const preview = useQuery<Plan, ApiError>({
    queryKey: ['convert-preview', leadId, createDeal, dealName, dealAmount],
    queryFn: () =>
      api.previewLeadConversion({
        path: { leadId },
        body: {
          createDeal,
          ...(dealName.trim() ? { dealName: dealName.trim() } : {}),
          ...(/^\d+(\.\d+)?$/.test(dealAmount.trim()) ? { dealAmount: dealAmount.trim() } : {}),
        },
      }),
    /*
     * The last plan stays on screen while the next one is fetched.
     *
     * The key holds three values that three controls below set, and one of
     * them is a text input - so without this the early return fired on every
     * keystroke and unmounted the field being typed into. A preview is meant
     * to follow what somebody is typing; one that removes the thing they are
     * typing into does not (P-27).
     */
    placeholderData: keepPreviousData,
  });

  const convert = useMutation({
    mutationFn: () =>
      api.convertLead({
        path: { leadId },
        body: {
          createDeal,
          ...(dealName.trim() ? { dealName: dealName.trim() } : {}),
          ...(/^\d+(\.\d+)?$/.test(dealAmount.trim()) ? { dealAmount: dealAmount.trim() } : {}),
        },
      }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['leads'] });
      void queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
      toast.show({
        tone: 'success',
        title: t('lead.converted'),
        body: t('convert.reversible'),
      });
      navigate(result.dealId ? `/deals/${result.dealId}` : `/contacts/${result.contactId}`);
    },
    onError: (error: ApiError) => {
      // Names what failed and keeps the dialog open with the input intact:
      // nothing was created, and re-typing an amount because a server said no
      // is a punishment for the server's problem.
      toast.show({ tone: 'error', title: t('convert.failed'), body: error.message });
    },
  });

  if (preview.isPending) return <ConvertLoading />;
  if (preview.isError) {
    return <ConvertError error={preview.error} retry={() => void preview.refetch()} />;
  }

  const plan = preview.data;
  const willCreate = [
    plan.account.action === 'create',
    plan.contact.action === 'create',
    plan.deal !== null,
  ].filter(Boolean).length;

  return (
    <div
      data-component="ConvertLead"
      role="dialog"
      aria-modal="true"
      aria-labelledby="convert-title"
      className="mx-auto flex w-full max-w-modal flex-col gap-4"
    >
      <header className="flex flex-col gap-1">
        <h1 id="convert-title" className="text-h1 text-ink">
          {t('convert.title')}
        </h1>
        <p className="text-dense text-ink-meta">{t('convert.body')}</p>
      </header>

      {/* Three preview cards, always in this order, always all present - a
          card that disappears when its record will not be created would make
          the absence easy to miss. The deal card says it will not be created
          instead. */}
      <PreviewCard
        kind="company"
        icon="Buildings"
        label={t('convert.company')}
        name={plan.account.name}
        action={plan.account.action}
        labels={{ create: t('convert.willCreate'), link: t('convert.willLink') }}
      />

      <PreviewCard
        kind="person"
        icon="User"
        label={t('convert.person')}
        name={plan.contact.name}
        detail={plan.contact.email}
        action={plan.contact.action}
        labels={{ create: t('convert.willCreate'), link: t('convert.willLink') }}
      />

      <Panel data-preview-card="deal">
        <div className="flex flex-col gap-2">
          <span className="flex items-center gap-2">
            <Icon name="Target" size="inline" decorative />
            <span className="text-micro uppercase tracking-label text-ink-label">
              {t('convert.deal')}
            </span>
            <label className="ms-auto inline-flex items-center gap-2 text-dense text-ink">
              <input
                type="checkbox"
                data-create-deal
                checked={createDeal}
                onChange={(event) => setCreateDeal(event.target.checked)}
                className="h-4 w-4 rounded-control border border-rule accent-bronze"
              />
              {t('convert.createDeal')}
            </label>
          </span>

          {plan.deal ? (
            <div className="flex flex-col gap-2">
              <label className="flex flex-col gap-1 text-dense text-ink">
                {t('convert.dealName')}
                <input
                  data-deal-name
                  value={dealName || plan.deal.name}
                  onChange={(event) => setDealName(event.target.value)}
                  className="h-input rounded-control border border-rule bg-surface px-3 text-body text-ink"
                />
              </label>
              <label className="flex flex-col gap-1 text-dense text-ink">
                {t('convert.dealAmount')}
                <input
                  data-deal-amount
                  inputMode="decimal"
                  value={dealAmount}
                  placeholder={formatMoney(plan.deal.amount, format, { currency: plan.deal.currency })}
                  onChange={(event) => setDealAmount(event.target.value)}
                  className="h-input rounded-control border border-rule bg-surface px-3 text-body text-ink"
                />
              </label>
              <Num className="text-meta text-ink-meta">{plan.deal.stageLabel}</Num>
            </div>
          ) : (
            <p data-no-deal className="text-body text-ink-meta">
              {t('convert.noDeal')}
            </p>
          )}
        </div>
      </Panel>

      {/* Whatever the server thought worth saying before anybody presses
          anything: a company that already exists, a lead with no contact
          details, a workspace with no pipeline. */}
      {plan.warnings.length > 0 ? (
        <ul data-warnings className="flex flex-col gap-1">
          {plan.warnings.map((warning) => (
            <li key={warning} className="flex items-start gap-2 text-dense text-warning-text">
              <Icon name="Warning" size="inline" decorative />
              {warning}
            </li>
          ))}
        </ul>
      ) : null}

      {/* The confirm is the shared dialog, not a button pair written here.
          Setup's switchboard confirms the same way, and two implementations of
          "are you sure" drift into one that focuses the destructive control. */}
      <ConfirmDialog
        open
        title={t('convert.title')}
        confirmLabel={willCreate === 3 ? t('convert.confirm') : t('convert.confirmTwo')}
        labels={{ cancel: t('action.cancel') }}
        busy={convert.isPending}
        onConfirm={() => convert.mutate()}
        onCancel={() => navigate(`/leads/${leadId}`)}
      >
        <span className="flex flex-col gap-1">
          <span>{t('convert.reversible')}</span>
        </span>
      </ConfirmDialog>
    </div>
  );
}

function PreviewCard({
  kind,
  icon,
  label,
  name,
  detail,
  action,
  labels,
}: {
  kind: string;
  icon: string;
  label: string;
  name: string;
  detail?: string | null;
  action: 'create' | 'link';
  labels: { create: string; link: string };
}): JSX.Element {
  return (
    <Panel data-preview-card={kind}>
      <div className="flex items-start gap-2">
        <Icon name={icon} size="inline" decorative />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-micro uppercase tracking-label text-ink-label">{label}</span>
          <span className="truncate text-body text-ink">{name}</span>
          {detail ? <span className="truncate text-meta text-ink-meta">{detail}</span> : null}
        </div>
        {/* "Will be created" and "already exists, will be used" are different
            outcomes, and a person about to merge a lead into the wrong company
            needs to see which one this is. */}
        <span
          data-action={action}
          className={
            action === 'create'
              ? 'inline-flex h-chip items-center rounded-chip bg-success-tint px-2 text-meta text-success-text'
              : 'inline-flex h-chip items-center rounded-chip bg-info-tint px-2 text-meta text-info-text'
          }
        >
          {action === 'create' ? labels.create : labels.link}
        </span>
      </div>
    </Panel>
  );
}

function ConvertLoading(): JSX.Element {
  const t = useT();
  return (
    <div
      className="mx-auto flex w-full max-w-modal flex-col gap-4"
      role="status"
      aria-live="polite"
      aria-busy
    >
      <span className="sr-only">{t('state.loading')}</span>
      <Skeleton className="h-line-h1 w-block-xl" />
      {Array.from({ length: 3 }, (_, index) => (
        <Skeleton key={index} className="h-block-xs w-full" />
      ))}
    </div>
  );
}

function ConvertEmpty(): JSX.Element {
  const t = useT();
  return <ConvertError error={new Error(t('record.notFound.body'))} retry={() => undefined} />;
}

function ConvertError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  const t = useT();
  return (
    <ErrorState
      title={t('convert.failed')}
      body={error instanceof Error ? error.message : t('record.notFound.body')}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-11',
  route: '/leads/:id/convert',
  surface: 'modal',
  chrome: 'bare',
  title: 'Convert lead',
  // F-035. SC-11 IS lead conversion; consent is a different capability that
  // this screen happens to respect.
  features: ['F-035'],
  entitlement: 'F-039',
  reachedBy: [{ kind: 'screen', from: 'SC-10', how: 'the Convert button on the lead' }],
  components: ['ConfirmDialog'],
  elements: ['ConvertLead'],
  sampleRecord: { objectKey: 'lead', param: 'id' },
  states: {
    empty: ConvertEmpty,
    loading: ConvertLoading,
    error: ConvertError,
  },
  component: ConvertLeadScreen,
});

export default screen;
